import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Edit, Loader2, Users, Search, ArrowLeft, Upload, MessageSquare, Star, Send } from "lucide-react";
import { toast } from "sonner";
import { usePagination, PaginationBar } from "@/components/PaginationBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectManagement({ userRole }) {
  const [progetti, setProgetti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ titolo: "", descrizione: "", stato: "attivo", avanzamento: 0 });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const fetch = async () => {
    try { const { data } = await axios.get(`${API}/progetti`); setProgetti(data); } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => {
    fetch();
    axios.get(`${API}/admin/users`).then(r => setAllUsers(r.data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm({ titolo: "", descrizione: "", stato: "attivo", avanzamento: 0 }); setDetail(null); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ titolo: p.titolo, descrizione: p.descrizione, stato: p.stato, avanzamento: p.avanzamento || 0 }); setDetail(null); setShowForm(true); };

  const handleSave = async () => {
    if (!form.titolo || !form.descrizione) { toast.error("Compila titolo e descrizione"); return; }
    setSaving(true);
    try {
      if (editing) { await axios.put(`${API}/progetti/${editing.id}`, form); toast.success("Aggiornato"); }
      else { await axios.post(`${API}/progetti`, form); toast.success("Creato"); }
      setShowForm(false); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await axios.delete(`${API}/progetti/${id}`); toast.success("Eliminato"); setDetail(null); fetch(); } catch { toast.error("Errore"); }
  };

  const getMemberNames = (ids) => (ids || []).map(id => { const u = allUsers.find(x => x.id === id); return u ? `${u.nome} ${u.cognome}` : id; });
  const sc = (s) => ({ attivo: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", in_corso: "bg-[#1a3a5c] text-[#5ba8d9] border-[#2a5080]", completato: "bg-white/5 text-white/40 border-white/10" }[s] || "");

  const searched = progetti.filter(p => `${p.titolo} ${p.stato}`.toLowerCase().includes(search.toLowerCase()));
  const pg = usePagination(searched);

  if (loading) return <div className="text-center py-12 text-white/30">Caricamento...</div>;

  if (detail) {
    const members = getMemberNames(detail.membri);
    const prog = detail.avanzamento || 0;
    return (
      <div data-testid="project-detail">
        <button onClick={() => setDetail(null)} className="flex items-center gap-1.5 text-sm text-white/40 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Torna</button>
        <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-6 shadow-lg shadow-black/20">
          <div className="flex items-start justify-between mb-4">
            <div><h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{detail.titolo}</h2><p className="text-xs text-white/30 mt-1">di {detail.created_by_name}</p></div>
            <div className="flex gap-2">
              <Badge className={`text-xs border ${sc(detail.stato)}`}>{detail.stato}</Badge>
              <Button size="sm" onClick={() => openEdit(detail)} className="bg-[#1a3a5c] text-[#5ba8d9] hover:bg-[#2a5080] border border-[#2a5080] text-xs"><Edit className="w-3 h-3 mr-1" /> Modifica</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            <div className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]"><p className="text-[10px] text-white/30 uppercase mb-1">Membri</p><p className="text-sm text-white/70 font-medium">{detail.membri?.length || 0}</p></div>
            <div className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]"><p className="text-[10px] text-white/30 uppercase mb-1">Stato</p><p className="text-sm text-white/70 font-medium capitalize">{detail.stato}</p></div>
            <div className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]"><p className="text-[10px] text-white/30 uppercase mb-1">Avanzamento</p>
              <div className="flex items-center gap-2 mt-1"><div className="flex-1 h-2 bg-[#1e3456] rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{width:`${prog}%`}} /></div><span className="text-xs text-emerald-400 font-bold">{prog}%</span></div>
            </div>
          </div>
          <div className="mb-5"><p className="text-[10px] text-white/30 uppercase mb-2">Descrizione</p><p className="text-sm text-white/60 leading-relaxed whitespace-pre-wrap bg-[#0f1d32] rounded-lg p-4 border border-[#1e3456]">{detail.descrizione}</p></div>
          {members.length > 0 && (
            <div><p className="text-[10px] text-white/30 uppercase mb-2">Membri ({members.length})</p>
              <div className="flex flex-wrap gap-2">{members.map((name, i) => (
                <div key={i} className="flex items-center gap-2 bg-[#0f1d32] rounded-lg px-3 py-1.5 border border-[#1e3456]">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center text-[9px] font-bold text-emerald-400">{name.split(' ').map(w=>w[0]).join('')}</div>
                  <span className="text-xs text-white/60">{name}</span>
                </div>
              ))}</div>
            </div>
          )}

          {/* Valutazioni + Commenti */}
          <ValutazioniSection tipo="progetto" rifId={detail.id} />
          <CommentiSection tipo="progetto" rifId={detail.id} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="project-management">
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" /><Input placeholder="Cerca progetti..." value={search} onChange={e => { setSearch(e.target.value); pg.resetPage(); }} className="pl-10 bg-white/5 border-[#1e3456] text-white placeholder:text-white/20" /></div>
        <Button onClick={openCreate} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 font-medium"><Plus className="w-4 h-4 mr-2" /> Nuovo Progetto</Button>
      </div>
      <div className="space-y-2">
        {pg.paged.map(p => {
          const prog = p.avanzamento || 0;
          return (
            <div key={p.id} className="bg-[#13243d] border border-[#1e3456] rounded-xl p-4 cursor-pointer hover:border-[#2a5080] hover:shadow-lg hover:shadow-black/15 transition-all" onClick={() => setDetail(p)} data-testid={`project-row-${p.id}`}>
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1"><p className="text-sm font-medium text-white truncate">{p.titolo}</p><Badge className={`text-[10px] border ${sc(p.stato)}`}>{p.stato}</Badge></div>
                  <div className="flex items-center gap-4 text-[10px] text-white/30"><span>di {p.created_by_name}</span><span className="flex items-center gap-1"><Users className="w-3 h-3" />{p.membri?.length || 0}</span></div>
                  {prog > 0 && <div className="mt-1.5 flex items-center gap-2"><div className="w-20 h-1 bg-[#1e3456] rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{width:`${prog}%`}} /></div><span className="text-[9px] text-emerald-400">{prog}%</span></div>}
                </div>
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="text-white/20 hover:text-[#5ba8d9] hover:bg-[#1a3a5c]"><Edit className="w-3.5 h-3.5" /></Button>
                  {userRole === "admin" && <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-white/20 hover:text-red-400 hover:bg-red-500/5"><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            </div>
          );
        })}
        {!progetti.length && <div className="text-center py-12 text-white/30">Nessun progetto</div>}
      </div>
      <PaginationBar {...pg} />

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg bg-[#13243d] border-[#1e3456] text-white">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>{editing ? "Modifica" : "Nuovo Progetto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-white/60">Titolo *</Label><Input value={form.titolo} onChange={e => setForm(p => ({ ...p, titolo: e.target.value }))} className="mt-1 bg-white/5 border-[#1e3456] text-white" /></div>
            <div><Label className="text-xs text-white/60">Descrizione *</Label><Textarea value={form.descrizione} onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))} className="mt-1 bg-white/5 border-[#1e3456] text-white" rows={3} /></div>
            <div><Label className="text-xs text-white/60">Stato</Label>
              <Select value={form.stato} onValueChange={v => setForm(p => ({ ...p, stato: v }))}>
                <SelectTrigger className="mt-1 bg-white/5 border-[#1e3456] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#13243d] border-[#1e3456] text-white"><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="in_corso">In Corso</SelectItem><SelectItem value="completato">Completato</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs text-white/60">Avanzamento</Label><span className="text-xs font-bold text-emerald-400">{form.avanzamento || 0}%</span></div>
              <input type="range" min={0} max={100} step={5} value={form.avanzamento || 0} onChange={e => setForm(p => ({ ...p, avanzamento: parseInt(e.target.value) }))} className="w-full h-1.5 bg-[#1e3456] rounded-full appearance-none cursor-pointer accent-emerald-500" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#1e3456] text-white/50 bg-transparent">Annulla</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#C9A84C] text-[#001830]">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Salva" : "Crea")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CommentiSection({ tipo, rifId }) {
  const [commenti, setCommenti] = useState([]);
  const [nc, setNc] = useState("");
  const [s, setS] = useState(false);
  useEffect(() => { if (rifId) axios.get(`${API}/commenti/${tipo}/${rifId}`).then(r => setCommenti(r.data)).catch(() => {}); }, [tipo, rifId]);
  const send = async () => { if (!nc.trim()) return; setS(true); try { const { data } = await axios.post(`${API}/commenti`, { testo: nc, riferimento_tipo: tipo, riferimento_id: rifId }); setCommenti(p => [...p, data]); setNc(""); toast.success("Aggiunto"); } catch (e) { console.error(e); } setS(false); };
  const del = async (id) => { try { await axios.delete(`${API}/commenti/${id}`); setCommenti(p => p.filter(c => c.id !== id)); } catch (e) { console.error(e); } };
  return (
    <div className="mt-5">
      <p className="text-[10px] text-white/30 uppercase mb-3 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Commenti ({commenti.length})</p>
      <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">{commenti.map(c => (
        <div key={c.id} className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2"><div className="w-5 h-5 rounded-full bg-[#1a3a5c] flex items-center justify-center text-[8px] font-bold text-[#5ba8d9]">{c.autore_nome?.split(' ').map(w=>w[0]).join('')}</div><span className="text-xs text-white/60">{c.autore_nome}</span><span className="text-[9px] text-white/20">{new Date(c.created_at).toLocaleString('it-IT',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</span></div>
            <Button size="sm" variant="ghost" onClick={() => del(c.id)} className="text-white/15 hover:text-red-400 h-5 w-5 p-0"><Trash2 className="w-3 h-3" /></Button>
          </div><p className="text-sm text-white/50 ml-7">{c.testo}</p>
        </div>
      ))}</div>
      <div className="flex gap-2"><Input value={nc} onChange={e => setNc(e.target.value)} placeholder="Commenta..." className="bg-white/5 border-[#1e3456] text-white placeholder:text-white/20 text-sm" onKeyDown={e => e.key==='Enter' && send()} />
        <Button onClick={send} disabled={s} size="sm" className="bg-[#1a3a5c] text-[#5ba8d9] hover:bg-[#2a5080] border border-[#2a5080]">{s?<Loader2 className="w-3.5 h-3.5 animate-spin"/>:<Send className="w-3.5 h-3.5"/>}</Button>
      </div>
    </div>
  );
}

function ValutazioniSection({ tipo, rifId }) {
  const [data, setData] = useState({ valutazioni: [], media: 0, totale: 0, mia_valutazione: null });
  const [mv, setMv] = useState(0);
  const [mc, setMc] = useState("");
  const [s, setS] = useState(false);
  useEffect(() => { if (rifId) axios.get(`${API}/valutazioni/${tipo}/${rifId}`).then(r => { setData(r.data); if (r.data.mia_valutazione) { setMv(r.data.mia_valutazione.voto); setMc(r.data.mia_valutazione.commento||""); } }).catch(() => {}); }, [tipo, rifId]);
  const send = async () => { if (!mv) return; setS(true); try { await axios.post(`${API}/valutazioni`, { riferimento_tipo: tipo, riferimento_id: rifId, voto: mv, commento: mc }); toast.success("Salvata!"); const { data: d } = await axios.get(`${API}/valutazioni/${tipo}/${rifId}`); setData(d); } catch (e) { console.error(e); } setS(false); };
  return (
    <div className="mt-5 bg-[#0f1d32] rounded-xl p-4 border border-[#1e3456]">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] text-white/30 uppercase flex items-center gap-1.5"><Star className="w-3.5 h-3.5" /> Valutazioni</p>
        {data.totale > 0 && <div className="flex items-center gap-2"><div className="flex">{[1,2,3,4,5].map(x=><Star key={x} className={`w-3.5 h-3.5 ${x<=Math.round(data.media)?'text-[#C9A84C] fill-[#C9A84C]':'text-white/10'}`}/>)}</div><span className="text-xs text-[#C9A84C] font-bold">{data.media}/5</span><span className="text-[9px] text-white/20">({data.totale})</span></div>}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex gap-1">{[1,2,3,4,5].map(x=><button key={x} onClick={()=>setMv(x)} className="p-0.5"><Star className={`w-5 h-5 transition-colors ${x<=mv?'text-[#C9A84C] fill-[#C9A84C]':'text-white/15 hover:text-[#C9A84C]/50'}`}/></button>)}</div>
        <Input value={mc} onChange={e=>setMc(e.target.value)} placeholder="Feedback..." className="flex-1 bg-white/5 border-[#1e3456] text-white text-xs placeholder:text-white/15 h-8" />
        <Button onClick={send} disabled={s||!mv} size="sm" className="bg-[#C9A84C] text-[#001830] h-8 text-xs">{s?<Loader2 className="w-3 h-3 animate-spin"/>:"Valuta"}</Button>
      </div>
    </div>
  );
}
